"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient?: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="231533" className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
          <div className="aspect-[3/4] bg-muted/40 animate-pulse rounded-xl" />
          <div className="flex flex-col space-y-8 pt-4 md:pt-10">
            <div className="space-y-4">
              <div className="h-12 bg-muted/40 animate-pulse rounded-md w-3/4" />
              <div className="h-8 bg-muted/40 animate-pulse rounded-md w-1/4" />
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-muted/40 animate-pulse rounded-md w-full" />
              <div className="h-4 bg-muted/40 animate-pulse rounded-md w-5/6" />
              <div className="h-4 bg-muted/40 animate-pulse rounded-md w-4/6" />
            </div>
            <div className="h-14 bg-muted/40 animate-pulse rounded-md w-full mt-4" />
            <Separator className="my-6" />
            <div className="space-y-3">
              <div className="h-5 bg-muted/40 animate-pulse rounded-md w-1/2" />
              <div className="h-5 bg-muted/40 animate-pulse rounded-md w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-destructive mb-4">Neuspjelo učitavanje detalja proizvoda.</p>
        <p className="text-muted-foreground text-sm">{error}</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-32 text-center flex flex-col items-center justify-center">
        <h2 className="font-default text-2xl font-semibold mb-4 text-foreground">Proizvod nije pronađen</h2>
        <p className="text-muted-foreground mb-8">Proizvod koji tražite ne postoji ili je uklonjen iz naše ponude.</p>
        <Link to="#">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">Povratak u trgovinu</Button>
        </Link>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="container mx-auto px-4 md:px-6 max-w-7xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
        {}
        <div className="relative w-full">
          <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-xl bg-muted/20 flex items-center justify-center border border-border/50">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover object-center transition-opacity duration-500"
                loading="eager"
              />
            ) : (
              <span className="text-muted-foreground font-default">Slika nije dostupna</span>
            )}
          </div>
        </div>

        {}
        <div className="flex flex-col pt-2 md:pt-8 lg:pt-12">
          <h1 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            {product.title}
          </h1>

          <div className="text-2xl font-semibold text-foreground mb-8">
            {product.discounted_price !== undefined && product.discounted_price < product.price ? (
              <div className="flex items-center gap-4">
                <span className="text-muted-foreground line-through text-xl font-normal">
                  {product.price.toFixed(2)}€
                </span>
                <span className="text-primary">{product.discounted_price.toFixed(2)}€</span>
              </div>
            ) : (
              <span className="text-primary">{product.price?.toFixed(2)}€</span>
            )}
          </div>

          {product.short_description && (
            <div
              className="text-muted-foreground text-base leading-relaxed mb-10 [&>p]:mb-4 last:[&>p]:mb-0 font-sans"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="mb-10">
            <AddToCartButton
              productId={product.id}
              showQuantity={true}
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-semibold rounded-md transition-all shadow-md hover:shadow-lg"
              text="Dodaj u košaricu"
            />
          </div>

          <Separator className="mb-8 bg-border/60" />

          {}
          <div className="space-y-5 text-sm mb-12">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-baseline gap-3">
                <span className="font-semibold text-foreground min-w-[80px] font-default">Kategorija</span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link key={cat.id} to={cat.link} data-index={i}>
                      <Badge variant="secondary" className="font-default font-normal hover:bg-accent hover:text-accent-foreground transition-colors text-xs px-3 py-1 bg-secondary text-secondary-foreground border-none">
                        {cat.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            {product.tags && product.tags.length > 0 && (
              <div className="flex items-baseline gap-3">
                <span className="font-semibold text-foreground min-w-[80px] font-default">Oznake</span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link key={tag.id} to={tag.link} data-index={i}>
                      <Badge variant="outline" className="font-default font-normal hover:bg-accent hover:text-accent-foreground transition-colors text-xs px-3 py-1 border-border text-muted-foreground">
                        {tag.name}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {}
          {product.long_description && (
            <div className="mt-auto pt-8 border-t border-border/50">
              <h2 className="font-default text-xl font-bold mb-6 text-foreground uppercase tracking-wider">Detalji proizvoda</h2>
              <div
                className="prose prose-sm sm:prose-base max-w-none text-muted-foreground font-sans
                           [&>p]:mb-5 last:[&>p]:mb-0 
                           [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-5 [&>ul>li]:mb-2
                           [&>h3]:font-default [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-foreground [&>h3]:mt-8 [&>h3]:mb-3
                           [&>h4]:font-default [&>h4]:text-base [&>h4]:font-semibold [&>h4]:text-foreground [&>h4]:mt-6 [&>h4]:mb-2"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const [wpQuery, setWpQuery] = useState<WP_Query | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      try {
        const currentParams = window.wvcClient.getCurrentWPQueryParams();
        setWpQuery(new WP_Query(currentParams));
      } catch (error) {
        console.error("Failed to initialize WP_Query:", error);
      }
    }
  }, []);

  return (
    <section id="single-product-template-section" className="bg-background text-foreground py-16 md:py-24 lg:py-32 min-h-screen">
      {wpQuery ? (
        <EcommerceProductsProvider wp_query={wpQuery}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      ) : (
        <div className="container mx-auto px-4 py-32 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Učitavanje...
            </span>
          </div>
        </div>
      )}
    </section>
  );
}